import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, ThumbsUp, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  fetchPublicPendingSuggestions,
  submitSuggestionSupport,
  type PublicForumSuggestion,
} from '../utils/forumSuggestionsDb';
import { getVisitorHash } from '../utils/visitorId';
import { appendOutboundUtm } from '../utils/outboundUrl';

interface Props {
  darkMode: boolean;
  refreshKey?: number;
}

const SUPPORTED_KEY_PREFIX = 'suggestion_supported_';

const SuggestedForumsPanel: React.FC<Props> = ({ darkMode, refreshKey = 0 }) => {
  const [suggestions, setSuggestions] = useState<PublicForumSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [supportingId, setSupportingId] = useState<string | null>(null);
  const visitorHash = getVisitorHash();

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { data, error } = await fetchPublicPendingSuggestions(visitorHash);
    if (error) {
      console.error('Pending suggestions:', error);
      setLoadError(error.message);
      toast.error(`Bekleyen öneriler yüklenemedi: ${error.message}`);
    }
    setSuggestions(data);
    setLoading(false);
  }, [visitorHash]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions, refreshKey]);

  const handleSupport = async (suggestion: PublicForumSuggestion) => {
    if (supportingId || suggestion.supportedByMe) return;

    setSupportingId(suggestion.id);
    const { ok, error } = await submitSuggestionSupport(suggestion.id, visitorHash);
    setSupportingId(null);

    if (ok) {
      localStorage.setItem(`${SUPPORTED_KEY_PREFIX}${suggestion.id}`, '1');
      setSuggestions((prev) =>
        prev
          .map((item) =>
            item.id === suggestion.id
              ? { ...item, supportCount: item.supportCount + 1, supportedByMe: true }
              : item
          )
          .sort((a, b) => b.supportCount - a.supportCount || b.created_at.localeCompare(a.created_at))
      );
      toast.success('Desteğiniz kaydedildi');
      return;
    }

    toast.error(error || 'Destek gönderilemedi');
  };

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';

  return (
    <aside
      className={`rounded-xl border p-5 sm:p-6 ${card}`}
      aria-labelledby="suggested-forums-heading"
    >
      <h2 id="suggested-forums-heading" className={`text-lg font-semibold ${text}`}>
        Topluluk önerileri
      </h2>
      <p className={`text-sm mt-1 mb-4 ${muted}`}>
        Listeye eklenmesini istediğiniz forumları destekleyin; en çok destek alanlar önceliklendirilir.
      </p>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
        </div>
      ) : loadError ? (
        <p className={`text-sm text-amber-600 dark:text-amber-400`}>
          Liste yüklenemedi. Sayfayı yenileyin; sorun sürerse site yöneticisine bildirin.
        </p>
      ) : suggestions.length === 0 ? (
        <p className={`text-sm ${muted}`}>
          Henüz bekleyen öneri yok. «Forum öner» sekmesinden ilk öneriyi gönderebilirsiniz.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
          {suggestions.map((suggestion) => {
            const supported =
              suggestion.supportedByMe || !!localStorage.getItem(`${SUPPORTED_KEY_PREFIX}${suggestion.id}`);
            const isBusy = supportingId === suggestion.id;

            return (
              <li
                key={suggestion.id}
                className={`rounded-lg border p-3 ${
                  darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className={`font-medium leading-snug ${text}`}>{suggestion.name}</h3>
                  {suggestion.url && (
                    <a
                      href={
                        appendOutboundUtm(suggestion.url) ?? suggestion.url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-indigo-500 hover:text-indigo-600"
                      title="Siteyi aç"
                      aria-label={`${suggestion.name} sitesini aç`}
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
                <div className={`mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs ${muted}`}>
                  <span>{suggestion.categoryName}</span>
                  {suggestion.software && <span>{suggestion.software}</span>}
                  <span>
                    {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true, locale: tr })}
                  </span>
                </div>
                <SuggestionSupportFooter
                  suggestion={suggestion}
                  muted={muted}
                  darkMode={darkMode}
                  supported={supported}
                  isBusy={isBusy}
                  onSupport={() => handleSupport(suggestion)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
};

function SuggestionSupportFooter({
  suggestion,
  muted,
  darkMode,
  supported,
  isBusy,
  onSupport,
}: {
  suggestion: PublicForumSuggestion;
  muted: string;
  darkMode: boolean;
  supported: boolean;
  isBusy: boolean;
  onSupport: () => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <span className={`inline-flex items-center gap-1 text-xs ${muted}`}>
        <Users size={14} />
        {suggestion.supportCount} destek
      </span>
      <button
        type="button"
        disabled={supported || isBusy}
        onClick={onSupport}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
          supported
            ? darkMode
              ? 'bg-emerald-900/40 text-emerald-300'
              : 'bg-emerald-100 text-emerald-800'
            : 'bg-indigo-600 text-white hover:bg-indigo-700'
        }`}
      >
        <ThumbsUp size={15} />
        {supported ? 'Desteklendi' : isBusy ? 'Kaydediliyor…' : 'Destekle'}
      </button>
    </div>
  );
}

export default SuggestedForumsPanel;
